import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { saveTokens, clearTokens } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed out
  const [meta, setMeta] = useState(null);
  const [people, setPeople] = useState([]);

  const refreshMeta = useCallback(async () => {
    const [m, d] = await Promise.all([api.get("/meta"), api.get("/users/directory")]);
    setMeta(m.data);
    setPeople(d.data);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      try {
        const r = await api.post("/auth/refresh", { refresh_token: localStorage.getItem("sc_rt") || "" });
        saveTokens(r.data);
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        clearTokens();
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setUser(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) refreshMeta().catch(() => {});
  }, [user, refreshMeta]);

  useEffect(() => {
    const onExpire = () => setUser(false);
    window.addEventListener("auth:expired", onExpire);
    return () => window.removeEventListener("auth:expired", onExpire);
  }, []);

  const can = useCallback(
    (...perms) => !!user && perms.some((p) => user.permissions?.includes("*") || user.permissions?.includes(p)),
    [user]
  );

  const login = useCallback((data) => {
    saveTokens(data);
    const { access_token, refresh_token, ...u } = data;
    setUser(u);
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    clearTokens();
    setUser(false);
    setMeta(null);
  };

  const marketName = (key) => meta?.markets?.find((m) => m.key === key)?.name || key;

  return (
    <AuthCtx.Provider value={{ user, setUser: login, can, meta, refreshMeta, people, logout, marketName }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
